import {
  getInquiryFieldsFromApplication,
  isInquiryComplete,
  toInquiryInputDate,
} from "./inquiryApplicationUtils";
import { getCommissionRowKey } from "./commissionRowUtils";

export const BATCH_STATUSES = [
  { id: "draft", label: "Draft", tone: "slate" },
  { id: "sent", label: "Sent", tone: "blue" },
  { id: "in_progress", label: "In progress", tone: "amber" },
  { id: "completed", label: "Completed", tone: "emerald" },
  { id: "cancelled", label: "Cancelled", tone: "rose" },
];

export const ITEM_STATUSES = [
  { id: "pending", label: "Pending", tone: "slate" },
  { id: "sent", label: "Sent", tone: "blue" },
  { id: "replied", label: "Replied", tone: "violet" },
  { id: "moved_to_commission", label: "In commission", tone: "emerald" },
  { id: "cancelled", label: "Cancelled", tone: "rose" },
];

const TONE_CLASSES = {
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
  blue: "bg-blue-50 text-blue-700 ring-blue-200",
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  violet: "bg-violet-50 text-violet-700 ring-violet-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  rose: "bg-rose-50 text-rose-700 ring-rose-200",
};

export function getStatusMeta(list, status) {
  const found = list.find((item) => item.id === status);
  return {
    id: status || "unknown",
    label: found?.label || String(status || "Unknown").replace(/_/g, " "),
    tone: found?.tone || "slate",
    className: TONE_CLASSES[found?.tone || "slate"],
  };
}

export function getBatchStatusMeta(status) {
  return getStatusMeta(BATCH_STATUSES, status);
}

export function getItemStatusMeta(status) {
  return getStatusMeta(ITEM_STATUSES, status);
}

/** Stable row key for multi-select (app + course + provider). */
export function getInquirySelectionKey(application = {}) {
  const rowKey = getCommissionRowKey(application);
  if (rowKey) return rowKey;

  const fields = getInquiryFieldsFromApplication(application);
  const appId = fields.applicationId ?? application.application_id ?? "";
  const courseId = fields.courseId ?? application.courseid ?? application.course_id ?? "";
  const providerId =
    fields.providerId ?? application.providerid ?? application.provider_id ?? "";
  return `${appId}|${courseId || 0}|${providerId || 0}`;
}

export function canSelectForInquiryBatch(application) {
  return isInquiryComplete(getInquiryFieldsFromApplication(application));
}

export function buildInquiryBatchItemFromApplication(application = {}) {
  const fields = getInquiryFieldsFromApplication(application);
  const applicationId = Number(fields.applicationId);
  if (!Number.isFinite(applicationId) || applicationId <= 0) {
    throw new Error("Missing application_id for batch item.");
  }

  const toOptionalNumber = (value) => {
    if (value == null || value === "") return undefined;
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  };

  const item = {
    application_id: applicationId,
    client_id: toOptionalNumber(fields.clientId),
    provider_id: toOptionalNumber(fields.providerId),
    course_id: toOptionalNumber(fields.courseId),
    application_course_id: toOptionalNumber(fields.applicationCourseId),
    application_queue_id: toOptionalNumber(application.queue_id),
    student_name: fields.clientName || undefined,
    student_id: fields.studentId || undefined,
    dob: toInquiryInputDate(fields.dob) || undefined,
    course_name: fields.course || undefined,
    intake: fields.intake || undefined,
    start_date: toInquiryInputDate(fields.start) || undefined,
    end_date: toInquiryInputDate(fields.end) || undefined,
    provider_name: fields.provider || undefined,
    tuition_fee:
      fields.tuitionFee != null && fields.tuitionFee !== ""
        ? Number(fields.tuitionFee)
        : undefined,
  };

  Object.keys(item).forEach((key) => {
    if (item[key] === undefined || item[key] === null || item[key] === "") {
      delete item[key];
    }
  });

  return item;
}

export const INQUIRY_OPEN_FILTERS = [
  { id: "open", label: "Open inquiry" },
  { id: "closed", label: "Closed inquiry" },
];

export function getBatchRowToneClass(periodStatus) {
  if (periodStatus === "open") {
    return "border-l-4 border-l-emerald-500 bg-emerald-50/50 hover:bg-emerald-50";
  }
  if (periodStatus === "closed") {
    return "border-l-4 border-l-slate-300 bg-slate-50/90 hover:bg-slate-100";
  }
  return "hover:bg-[#F8FAFC]";
}

export const PERIOD_STATUSES = [
  { id: "open", label: "Open", tone: "emerald" },
  { id: "closed", label: "Closed", tone: "slate" },
];

export function getPeriodStatusMeta(status) {
  return getStatusMeta(PERIOD_STATUSES, status);
}

export function inquiryPeriodToMonthInput(period) {
  const value = String(period || "").trim();
  return /^\d{4}-\d{2}$/.test(value) ? value : "";
}

export function monthInputToInquiryPeriod(value) {
  const trimmed = String(value || "").trim();
  return /^\d{4}-\d{2}$/.test(trimmed) ? trimmed : "";
}

export function formatInquiryPeriodRangeLabel(start, end) {
  const startKey = monthInputToInquiryPeriod(start) || String(start || "").trim();
  const endKey =
    monthInputToInquiryPeriod(end) ||
    String(end || "").trim() ||
    startKey;

  const startLabel = formatInquiryPeriodLabel(startKey);
  if (!startKey || !endKey || endKey === startKey || endKey < startKey) {
    return startLabel;
  }

  return `${startLabel} – ${formatInquiryPeriodLabel(endKey)}`;
}

export function isInquiryPeriodRangeValid(start, end) {
  const startKey = monthInputToInquiryPeriod(start);
  if (!startKey) return false;

  const endKey = monthInputToInquiryPeriod(end) || startKey;
  return endKey >= startKey;
}

export function formatInquiryPeriodLabel(period) {
  const value = String(period || "").trim();
  if (!value) return "—";
  if (!/^\d{4}-\d{2}$/.test(value)) return value;

  const [year, month] = value.split("-").map(Number);
  const date = new Date(year, Math.max(0, month - 1), 1);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

export function currentInquiryPeriodKey() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

export function nextInquiryPeriodKey(period) {
  const value = String(period || currentInquiryPeriodKey()).trim();
  if (!/^\d{4}-\d{2}$/.test(value)) return currentInquiryPeriodKey();

  let [year, month] = value.split("-").map(Number);
  month += 1;
  if (month > 12) {
    month = 1;
    year += 1;
  }
  return `${year}-${String(month).padStart(2, "0")}`;
}

export function buildDefaultInquiryBatchName({ providerLabel = "" } = {}) {
  return String(providerLabel || "").trim();
}

export function buildCreateInquiryBatchPayload({
  applications = [],
  batchName = "",
  notes = "",
  markSent = false,
  applicationType = "education",
  periodStatus = "open",
}) {
  const items = applications.map(buildInquiryBatchItemFromApplication);
  if (!items.length) {
    throw new Error("Select at least one application.");
  }

  const name = String(batchName || "").trim();
  if (!name) {
    throw new Error("Enter a batch name.");
  }

  return {
    application_type: applicationType || "education",
    batch_name: name,
    notes: String(notes || "").trim() || undefined,
    mark_sent: Boolean(markSent),
    period_status: periodStatus || "open",
    items,
  };
}

export function formatBatchDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function defaultInquiryBatchSubject(count) {
  const today = new Date().toISOString().slice(0, 10);
  const n = Number(count) || 0;
  return `Commission inquiry — ${n} student${n === 1 ? "" : "s"} — ${today}`;
}

export function groupSelectedByProvider(applications = []) {
  const map = new Map();
  applications.forEach((app) => {
    const fields = getInquiryFieldsFromApplication(app);
    const key = String(fields.providerId || fields.provider || "unknown");
    const label = fields.provider || "Unknown provider";
    if (!map.has(key)) {
      map.set(key, { key, label, count: 0 });
    }
    map.get(key).count += 1;
  });
  return Array.from(map.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

export function getBatchItemProviderKey(item = {}) {
  if (item.provider_id != null && item.provider_id !== "") {
    return `id:${item.provider_id}`;
  }
  return `name:${String(item.provider_name || "unknown")
    .trim()
    .toLowerCase()}`;
}

export function areSameProviderBatchItems(items = []) {
  if (!items.length) return true;
  const key = getBatchItemProviderKey(items[0]);
  return items.every((item) => getBatchItemProviderKey(item) === key);
}

export function getConsecutiveProviderItems(items = [], providerName = "", startIndex = 0) {
  const group = [];
  for (let i = startIndex; i < items.length; i += 1) {
    if (String(items[i]?.provider_name || "") !== String(providerName || "")) {
      break;
    }
    group.push(items[i]);
  }
  return group;
}

/** Line items awaiting a provider reply (email already sent). */
export function isReplyableBatchItem(itemOrStatus) {
  const status =
    typeof itemOrStatus === "string"
      ? itemOrStatus
      : itemOrStatus?.item_status;
  return status === "sent";
}

export const BATCH_ITEM_LINE_FILTERS = [
  { id: "", label: "All statuses" },
  { id: "pending", label: "Open" },
  { id: "sent", label: "Sent" },
  { id: "replied", label: "Replied" },
];

export function matchesBatchItemStatusFilter(item = {}, filterId = "") {
  const status = item.item_status;
  if (!filterId) return true;
  if (filterId === "pending") return status === "pending";
  if (filterId === "sent") return status === "sent";
  if (filterId === "replied") {
    return ["replied", "moved_to_commission"].includes(status);
  }
  return true;
}

export function filterBatchItemsByStatus(items = [], filterId = "") {
  if (!filterId) return items;
  return items.filter((item) => matchesBatchItemStatusFilter(item, filterId));
}
